import { Chip, IconButton, Spinner } from '@material-tailwind/react';
import React, { useState } from 'react';
import { useGetRoles, useDeleteRole } from '../../../custom-hooks/use-roles';
import { PencilIcon, TrashIcon, ShieldIcon } from 'lucide-react';
import RoleFormModal from './roleFormModal';
import { PageHeader } from '../../../components';
import { usePermissions } from '../../../custom-hooks/use-permissions';
import { ProtectedRoutes } from '../../../router-x/routes';

const INITIAL_FORM_DATA = {
    name: '',
    description: '',
    permissions: [],
};

const SettingsRoles = () => {
    const [openFormModal, setOpenFormModal] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [selectedRoleId, setSelectedRoleId] = useState(null);

    const { data: roles, isLoading, error } = useGetRoles();
    const deleteRoleMutation = useDeleteRole();
    const { can } = usePermissions();

    const openAddModal = () => {
        setOpenFormModal(true);
        setFormData(INITIAL_FORM_DATA);
        setIsUpdating(false);
    };

    const getRolePageCount = (permissions = []) => {
        if (!permissions) return 0;
        return ProtectedRoutes.filter(route => 
            route.inSideBarList && permissions.some(p => p.startsWith(route.id))
        ).length;
    };

    const openEditModal = (role) => {
        setOpenFormModal(true);
        setFormData(role);
        setIsUpdating(true);
        setSelectedRoleId(role._id);
    };

    const handleDelete = async (role) => {
        if (role.isSystemRole) return;
        if (!window.confirm(`Are you sure you want to delete the role "${role.name}"?`)) return;
        try {
            await deleteRoleMutation.mutateAsync(role._id);
        } catch (error) {
            console.error("Error deleting role:", error);
        }
    };

    return (
        <div className='m-4 p-4 border rounded-3xl'>
            <PageHeader
                title="Role Management"
                isLoading={isLoading}
                actionOnClick={can('FX22-3', 'manage') ? openAddModal : undefined}
                actionType="add"
                actionText="Create New Role"
            />

            <div className='mt-4'>
                <div className='overflow-x-auto'>
                    {
                        isLoading ?
                            <div className='flex justify-center items-center h-full'>
                                <Spinner />
                            </div>
                            : error ?
                                <div className='flex justify-center items-center h-full'>
                                    <p className='text-red-500'>{error.message}</p>
                                </div>
                                :
                                <table className="w-full min-w-max table-auto text-left">
                                    <thead className="text-sm">
                                        <tr className="py-3">
                                            <th colSpan={5} className="py-3 text-xs font-light text-orange-900">
                                                List of All Roles ({roles?.length || 0})
                                            </th>
                                        </tr>
                                        <tr className="py-3 uppercase">
                                            <th className="py-3">Role Name</th>
                                            <th className="py-3">Description</th>
                                            <th className="py-3">Type</th>
                                            <th className="py-3">Permissions</th>
                                            <th className="py-3">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            roles?.map((role) => (
                                                <tr key={role._id} className="border-t hover:bg-gray-50 button rounded-full">
                                                    <td className="font-thin py-3 text-sm">
                                                        <div className='flex items-center gap-2'>
                                                            <ShieldIcon size={14} className={role.isSystemRole ? 'text-orange-600' : 'text-gray-400'} />
                                                            {role.name}
                                                        </div>
                                                    </td>
                                                    <td className="font-thin py-3 text-sm">{role.description}</td>
                                                    <td className="py-3">
                                                        <Chip
                                                            size='sm'
                                                            variant='ghost'
                                                            color={role.isSystemRole ? 'orange' : 'blue'}
                                                            value={role.isSystemRole ? 'System' : 'Custom'}
                                                            className='w-fit rounded-full'
                                                        />
                                                    </td>
                                                    <td className="font-thin py-3 text-sm">
                                                        {getRolePageCount(role.permissions)} pages
                                                    </td>
                                                    <td className="py-3 flex gap-1">
                                                        {can('FX22-3', 'manage') && (
                                                            <IconButton variant='gradient' color='orange' className='rounded-full' onClick={() => openEditModal(role)}>
                                                                <PencilIcon size={12} />
                                                            </IconButton>
                                                        )}
                                                        {can('FX22-3', 'manage') && !role.isSystemRole && (
                                                            <IconButton
                                                                variant='gradient'
                                                                color='red'
                                                                className='rounded-full'
                                                                onClick={() => handleDelete(role)}
                                                                loading={deleteRoleMutation.isLoading}
                                                            >
                                                                <TrashIcon size={12} />
                                                            </IconButton>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        }
                                    </tbody>
                                </table>
                    }
                </div>
            </div>
            {
                openFormModal &&
                <RoleFormModal
                    open={openFormModal}
                    onClose={() => setOpenFormModal(false)}
                    isUpdating={isUpdating}
                    defaultData={formData}
                    selectedRoleId={selectedRoleId}
                />
            }
        </div>
    );
};

export default SettingsRoles;
